/// <reference lib="dom" />

// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import {
  executeDiffclawOperation,
  type DiffclawArtifactExport,
  type DiffclawOperationId,
  type DiffclawOperationRequest,
  type DiffclawHttpComponents,
} from "@diffclaw/client";
import type { PackedReviewProtocol } from "@diffclaw/review-protocol-schema/v2";

export type FinalReviewPreview =
  DiffclawHttpComponents["schemas"]["CollaborationFinalReviewPreview"];
export type ReviewerProfileReference =
  DiffclawHttpComponents["schemas"]["ReviewerProfileReference"];
export type ProjectPolicy = DiffclawHttpComponents["schemas"]["ProjectPolicy"];
export type ServiceCredentialRequest =
  DiffclawHttpComponents["schemas"]["IssueServiceCredentialRequest"];
export interface ServiceCredentialGrant {
  readonly actor: { readonly actorId: string };
  readonly credentialId: string;
  readonly expiresAt: string;
  readonly grants: readonly string[];
  readonly token?: string;
}

export interface CockpitIdentity {
  readonly actorId: string;
  readonly authentication: "local-bootstrap" | "session";
  readonly organizationId: string;
  readonly projectId: string;
}

export interface CockpitScope {
  readonly organizationId: string;
  readonly projectId: string;
}

export interface HealthStatus {
  readonly checkedAt: string;
  readonly status: "live" | "ready";
}

export interface BrowserLoginChallenge {
  readonly authorizationUrl: string;
  readonly expiresAt: string;
  readonly state: string;
  readonly surface: "browser";
}

export interface BrowserSession {
  readonly absoluteExpiresAt: string;
  readonly actor: {
    readonly actorId: string;
    readonly actorType: "human";
    readonly authentication: "github-browser-session";
    readonly organizationId: string;
  };
  readonly idleExpiresAt: string;
  readonly sessionId: string;
}

export interface ChronicleReadiness {
  readonly canonicalRevision: number;
  readonly coverage: readonly {
    readonly category: string;
    readonly discovered: number;
    readonly ingested: number;
    readonly status: string;
  }[];
  readonly currentCode: "current-code-ready" | "limited" | "not-started";
  readonly historicalBackfill: "complete" | "in-progress" | "not-started";
  readonly projection: "current" | "degraded" | "not-built";
  readonly unavailableSources: readonly string[];
  readonly updatedAt: string;
}

export interface ChronicleBackfill {
  readonly backfillId: string;
  readonly cancelledAt: string | null;
  readonly completedAt: string | null;
  readonly createdAt: string;
  readonly knowledgeWatermark: number;
  readonly reason: string | null;
  readonly revision: number;
  readonly sourceScopeRevision: number;
  readonly sources: readonly {
    readonly cursor: string | null;
    readonly discovered: number;
    readonly inaccessibleIntervals: readonly {
      readonly from: string;
      readonly reason: string;
      readonly until: string | null;
    }[];
    readonly ingested: number;
    readonly kind: "git" | "github";
    readonly rateLimit: {
      readonly reason: string;
      readonly remaining: number | null;
      readonly resetAt: string;
    } | null;
    readonly sourceId: string;
    readonly state:
      "completed" | "pending" | "rate-limited" | "running" | "unavailable";
    readonly updatedAt: string;
  }[];
  readonly startedAt: string;
  readonly state: "cancelled" | "completed" | "degraded" | "paused" | "running";
  readonly updatedAt: string;
}

export interface ChronicleBackfillResult {
  readonly backfill: ChronicleBackfill;
  readonly readiness: ChronicleReadiness;
}

export interface ChronicleEvidenceReference {
  readonly atomId: string;
  readonly category: string;
  readonly excerpt: string | null;
  readonly path: string;
  readonly source: {
    readonly kind: "git" | "github";
    readonly objectId: string;
    readonly recordKind: string;
    readonly sourceId: string;
  } | null;
  readonly sourceAuthority:
    "organization-recorded" | "provider-observed" | "repository-authored";
  readonly validFrom: string;
  readonly validUntil: string | null;
}

export interface ChronicleCalibrationClaimEntry {
  readonly authority: {
    readonly actorId: string | null;
    readonly kind: "deterministic-source" | "maintainer-approved" | "proposed";
  };
  readonly claimId: string;
  readonly confidence: number;
  readonly evidence: readonly ChronicleEvidenceReference[];
  readonly kind: string;
  readonly object: string;
  readonly predicate: string;
  readonly status: "active" | "candidate";
  readonly subject: string;
  readonly validFrom: string;
}

export interface ChronicleCalibrationClaimSection {
  readonly entries: readonly ChronicleCalibrationClaimEntry[];
  readonly total: number;
  readonly truncated: boolean;
}

export interface ChronicleCalibrationInspection {
  readonly architectureMap: readonly string[];
  readonly candidateConventionClaimIds: readonly string[];
  readonly contradictionClaimIds: readonly string[];
  readonly coverage: ChronicleReadiness["coverage"];
  readonly evidenceSummary: {
    readonly architecture: ChronicleCalibrationClaimSection;
    readonly generatedAt: string;
    readonly history: {
      readonly categories: readonly {
        readonly category: string;
        readonly count: number;
      }[];
      readonly recent: readonly ChronicleEvidenceReference[];
      readonly total: number;
    };
    readonly inventory: {
      readonly canonicalRevision: number;
      readonly evidenceAtomCount: number;
      readonly projectClaimCount: number;
      readonly repositoryRevision: string | null;
      readonly updatedAt: string;
    };
    readonly learning: {
      readonly activeConventionCount: number;
      readonly approvedConventionCount: number;
      readonly candidateConventionCount: number;
      readonly observationCount: number;
      readonly proposalCount: number;
      readonly status:
        | "active"
        | "awaiting-maintainer"
        | "no-promoted-learning"
        | "observations-recorded";
    };
    readonly ownership: ChronicleCalibrationClaimSection;
    readonly product: ChronicleCalibrationClaimSection;
    readonly reviews: {
      readonly dispositions: readonly {
        readonly count: number;
        readonly disposition: string;
      }[];
      readonly recent: readonly {
        readonly disposition: string;
        readonly episodeId: string;
        readonly evidence: readonly ChronicleEvidenceReference[];
        readonly observedAt: string;
        readonly rationale: string;
        readonly sourceObjectId: string;
      }[];
      readonly total: number;
    };
  };
  readonly featureChronology: readonly string[];
  readonly generatedAt: string;
  readonly productMap: readonly string[];
  readonly reviewThemes: readonly string[];
  readonly uncertainties: readonly string[];
}

export interface LearningProposal {
  readonly contradictionEvidenceAtomIds: readonly string[];
  readonly createdAt: string;
  readonly outcome: "harmful-pattern" | "neutral" | "successful-pattern";
  readonly proposalId: string;
  readonly revision: number;
  readonly source: {
    readonly kind: string;
    readonly reference: string;
    readonly revision: string;
  };
  readonly sourceObservationIds: readonly string[];
  readonly state:
    "active" | "approved" | "decayed" | "proposed" | "retired" | "revised";
  readonly summary: string;
  readonly supersedesProposalId: string | null;
  readonly target:
    "convention" | "evaluation-case" | "project-claim" | "protocol-change";
  readonly updatedAt: string;
  readonly transitions: readonly {
    readonly actorId: string;
    readonly at: string;
    readonly evaluationPromotion: {
      readonly approvedAt: string;
      readonly approvedBy: string;
      readonly promotionId: string;
      readonly runId: string;
    } | null;
    readonly from: LearningProposal["state"] | null;
    readonly reason: string;
    readonly to: LearningProposal["state"];
  }[];
}

export interface ChronicleLearningObservation {
  readonly confidence: number;
  readonly contradictionObservationIds: readonly string[];
  readonly corroboratingObservationIds: readonly string[];
  readonly evidenceAtomIds: readonly string[];
  readonly observationId: string;
  readonly recordedAt: string;
  readonly revision: number;
  readonly sourceReviewEpisodeIds: readonly string[];
  readonly statement: string;
  readonly status: string;
}

export interface ChronicleLearningRun {
  readonly budgetMicros: number;
  readonly cancellationReason: string | null;
  readonly completedAt: string | null;
  readonly createdAt: string;
  readonly eligibleEpisodes: readonly {
    readonly contentDigest: string;
    readonly episodeId: string;
  }[];
  readonly failure: {
    readonly attempt: number;
    readonly code: string;
    readonly sourceRecordId: string;
    readonly retryAt: string | null;
  } | null;
  readonly mode: "historical" | "incremental";
  readonly modelId: string;
  readonly processedEpisodeCount: number;
  readonly rejectedEpisodeCount: number;
  readonly revision: number;
  readonly runId: string;
  readonly spentMicros: number;
  readonly state:
    | "budget-exhausted"
    | "cancelled"
    | "completed"
    | "failed"
    | "paused"
    | "running";
  readonly updatedAt: string;
}

export interface ChronicleLearningInspection {
  readonly activeRunId: string | null;
  readonly eligibleEpisodeCount: number;
  readonly observations: readonly ChronicleLearningObservation[];
  readonly observationTotal: number;
  readonly proposals: readonly {
    readonly evaluationTarget: {
      readonly digest: string;
      readonly identity: string;
      readonly kind: "learning";
      readonly revision: string;
    };
    readonly proposal: LearningProposal;
  }[];
  readonly proposalTotal: number;
  readonly runs: readonly ChronicleLearningRun[];
  readonly runTotal: number;
  readonly semanticProjection: {
    readonly encoderRevision: string | null;
    readonly projectionRevision: number | null;
    readonly state: "current" | "not-built" | "stale" | "unavailable";
  };
  readonly truncated: boolean;
}

export interface PersonalCoachingMemory {
  readonly content: string;
  readonly correction: {
    readonly correctedAt: string;
    readonly reason: string;
  } | null;
  readonly kind: "recurring-omission" | "successful-pattern";
  readonly memoryId: string;
  readonly retentionUntil: string;
  readonly revision: number;
  readonly updatedAt: string;
}

export interface PersonalCoachingView {
  readonly consent: {
    readonly expiresAt: string;
    readonly revision: number;
    readonly state: "active" | "revoked";
    readonly updatedAt: string;
  } | null;
  readonly memories: readonly PersonalCoachingMemory[];
  readonly tombstones: readonly {
    readonly deletedAt: string;
    readonly memoryId: string;
    readonly reason: string;
  }[];
  readonly uses: readonly {
    readonly generatedAt: string;
    readonly memoryIds: readonly string[];
    readonly objectivePolicyDigest: string;
    readonly useId: string;
  }[];
}

export interface PersonalCoachingResponse {
  readonly generatedAt: string;
  readonly guidance: readonly {
    readonly kind: PersonalCoachingMemory["kind"];
    readonly memoryId: string;
    readonly statement: string;
  }[];
  readonly objectivePolicyDigest: string;
  readonly reviewPolicyChanged: false;
  readonly useId: string;
}

export interface EvaluationCorpus {
  readonly cases: readonly {
    readonly caseId: string;
    readonly expectedFindings: readonly unknown[];
  }[];
  readonly contentDigest: string;
  readonly corpusId: string;
  readonly createdAt: string;
  readonly description: string;
  readonly partition: string;
  readonly visibility: "private" | "public";
}

export interface EvaluationMetrics {
  readonly acceptanceRate: number;
  readonly attentionMean: number;
  readonly chronicle: {
    readonly contradictionHandling: number;
    readonly downstreamReviewEffect: number;
    readonly factualCorrectness: number;
    readonly privacy: number;
    readonly provenance: number;
    readonly retrievalRelevance: number;
    readonly temporalCorrectness: number;
  };
  readonly computeMillisTotal: number;
  readonly costUsdTotal: number;
  readonly duplicateFreedom: number;
  readonly evidenceValidity: number;
  readonly failureRate: number;
  readonly latencyMillisMean: number;
  readonly latencyMillisP95: number;
  readonly locationAccuracy: number;
  readonly precision: number;
  readonly publicationAccuracy: number;
  readonly severityWeightedRecall: number;
}

export interface EvaluationRun {
  readonly corpusDigest: string;
  readonly corpusId: string;
  readonly expectedSampleCount: number;
  readonly incomplete: boolean;
  readonly metrics: EvaluationMetrics | null;
  readonly mode: "deterministic" | "stochastic";
  readonly observations: readonly unknown[];
  readonly resultDigest: string | null;
  readonly revision: number;
  readonly runId: string;
  readonly state: "cancelled" | "completed" | "partial" | "running";
  readonly target: {
    readonly digest: string;
    readonly identity: string;
    readonly kind: string;
    readonly revision: string;
  };
  readonly uncertainty: {
    readonly metricVariance: Readonly<Record<string, number>>;
    readonly sampleSize: number;
  } | null;
  readonly updatedAt: string;
}

export interface EvaluationPromotion {
  readonly approvedAt: string;
  readonly approvedBy: string;
  readonly baselineRunId: string | null;
  readonly decision: "accepted" | "rejected" | "shadow";
  readonly promotionId: string;
  readonly rationale: string;
  readonly runId: string;
  readonly thresholds: readonly {
    readonly comparator: "maximum" | "minimum";
    readonly metric: string;
    readonly value: number;
  }[];
  readonly violations: readonly string[];
}

export interface CockpitReviewListItem {
  readonly additions: number;
  readonly blindSpotCount: number;
  readonly currentReviewRunId: string | null;
  readonly currentReviewSubjectId: string;
  readonly declaredIntentSummary: string;
  readonly deletions: number;
  readonly findingCount: number;
  readonly headRevision: string;
  readonly reviewSeriesId: string;
  readonly runState: string;
  readonly synchronizedAt: string;
  readonly updatedAt: string;
}

export interface ProjectSummary {
  readonly createdAt: string;
  readonly name: string;
  readonly organizationId: string;
  readonly projectId: string;
  readonly revision: number;
}

export interface SubjectChange {
  readonly additions: number;
  readonly changedContracts: readonly string[];
  readonly changedSymbols: readonly string[];
  readonly changeKind: string;
  readonly deletions: number;
  readonly material:
    | {
        readonly availability: "available";
        readonly mediaType: string;
        readonly sha256: string;
        readonly sizeBytes: number;
        readonly source: string;
      }
    | {
        readonly availability: "unavailable";
        readonly reason: string;
      };
  readonly path: string;
  readonly previousPath: string | null;
  readonly surfaces: readonly string[];
}

export interface ReviewSubject {
  readonly baseRevision: string;
  readonly changes: readonly SubjectChange[];
  readonly declaredIntent: {
    readonly linkedRequirements: readonly string[];
    readonly summary: string;
  };
  readonly headRevision: string;
  readonly repositoryConnectionIds: readonly string[];
  readonly reviewSubjectId: string;
}

export interface ReviewRun {
  readonly reviewerProfile?: DiffclawHttpComponents["schemas"]["ReviewReviewerProfileSelection"];
  readonly reviewRunId: string;
  readonly revision: number;
  readonly state: string;
  readonly updatedAt: string;
}

export interface ReviewRunInspection {
  readonly outcome: {
    readonly usage?: DiffclawHttpComponents["schemas"]["ReviewRunOutcome"]["usage"];
    readonly profileExecutions?: readonly DiffclawHttpComponents["schemas"]["ReviewProfileExecution"][];
    readonly obligationResults?: readonly DiffclawHttpComponents["schemas"]["ReviewObligationResult"][];
    readonly coverage: {
      readonly examinedPaths: readonly string[];
      readonly missingContext: readonly string[];
      readonly unexaminedPaths: readonly string[];
    };
    readonly findingIds: readonly string[];
    readonly publication: string;
    readonly unavailableChecks: readonly string[];
  } | null;
  readonly plan: {
    readonly changeModel: {
      readonly affectedBehaviors: readonly string[];
      readonly logicalGroups: readonly {
        readonly groupId: string;
        readonly paths: readonly string[];
        readonly rationale: string;
        readonly surfaces: readonly string[];
      }[];
      readonly reviewOrder: readonly string[];
      readonly riskMap: readonly {
        readonly level: "high" | "low" | "medium";
        readonly paths: readonly string[];
        readonly reasons: readonly string[];
        readonly riskId: string;
      }[];
    };
    readonly changeNarrative: string;
    readonly chronicleSnapshot: {
      readonly canonicalRevision: number;
      readonly historicalBackfill: string;
      readonly knowledgeWatermark: number;
      readonly learningContext: {
        readonly activeLearning: readonly {
          readonly evaluationPromotionId: string;
          readonly evidenceAtomIds: readonly string[];
          readonly outcome: string;
          readonly proposalId: string;
          readonly summary: string;
          readonly target: string;
        }[];
        readonly activeLearningState: "current" | "unavailable";
        readonly activeLearningTruncated: boolean;
        readonly relatedReviews: readonly {
          readonly disposition: string;
          readonly episodeId: string;
          readonly observedAt: string;
          readonly rationale: string;
          readonly retrieval: "lexical-fallback" | "semantic";
          readonly similarity: number;
          readonly sourceObjectId: string;
        }[];
        readonly relatedReviewsTruncated: boolean;
        readonly retrieval: {
          readonly encoderRevision: string | null;
          readonly projectionRevision: number | null;
          readonly reasonCode: string | null;
          readonly state: "current" | "degraded" | "unavailable";
        };
      };
      readonly projectionReadiness: string;
      readonly unavailableSources: readonly string[];
    };
    readonly protocolSelections: readonly {
      readonly reviewerProfilePackage?: PackedReviewProtocol;
      readonly knownLimitations: readonly string[];
      readonly protocolId: string;
      readonly protocolVersion: string;
    }[];
  } | null;
  readonly run: ReviewRun;
}

export interface ConversationView {
  readonly finalReviewPreviews?: readonly FinalReviewPreview[];
  readonly actions: readonly {
    readonly kind: string;
    readonly outcomeCode: string;
    readonly state: string;
  }[];
  readonly conversation: {
    readonly conversationId: string;
    readonly currentContext: {
      readonly finding: {
        readonly currentDisposition: string;
        readonly findingRevision: number;
        readonly sourceLocations: readonly {
          readonly endLine: number;
          readonly path: string;
          readonly side: "base" | "head";
          readonly startLine: number;
        }[];
      };
      readonly review: {
        readonly reviewSubjectId: string;
      };
    };
    readonly findingId: string;
    readonly revision: number;
    readonly state: string;
    readonly updatedAt: string;
  };
  readonly deliveryIntents: readonly {
    readonly finalReviewActionId?: string;
    readonly intentId: string;
    readonly lastErrorCode: string | null;
    readonly state: string;
    readonly updatedAt: string;
  }[];
  readonly fixProposals: readonly {
    readonly affectedPaths: readonly string[];
    readonly artifactType:
      "branch" | "code-suggestion" | "patch" | "regression-test";
    readonly boundedIntent: string;
    readonly proposalPaths: readonly string[];
    readonly generation: {
      readonly alternatives: readonly string[];
      readonly behaviorDelta: string;
      readonly conflicts: readonly string[];
      readonly explanation: string;
      readonly generatorIdentity: string;
      readonly patchDigest: string;
    } | null;
    readonly proposalId: string;
    readonly publicationAuthorization: {
      readonly action:
        "code-suggestion" | "update-pull-request" | "write-branch";
      readonly authorizationId: string;
      readonly consumedAt: string | null;
      readonly expiresAt: string;
      readonly state: "active" | "consumed" | "expired" | "revoked";
    } | null;
    readonly publicationReference: string | null;
    readonly revision: number;
    readonly state:
      | "generation-failed"
      | "publication-authorized"
      | "publication-failed"
      | "published"
      | "stale"
      | "validation-failed"
      | "verified";
    readonly targetBranch: string | null;
    readonly targetHeadRevision: string;
    readonly validation: {
      readonly checks: readonly {
        readonly checkId: string;
        readonly kind: "affected-test" | "reproduction";
        readonly reasonCode: string;
        readonly state:
          "failed" | "passed" | "skipped" | "timed-out" | "unsupported";
      }[];
      readonly validatorIdentity: string;
      readonly verifiedAt: string;
    } | null;
  }[];
  readonly messages: readonly {
    readonly actorId: string;
    readonly body: string;
    readonly kind: string;
    readonly messageId: string;
    readonly occurredAt: string;
  }[];
  readonly privateNotes: readonly {
    readonly body: string;
    readonly noteId: string;
    readonly revision: number;
  }[];
}

export interface CockpitReviewWorkspace {
  readonly blindSpots: readonly string[];
  readonly conversations: readonly ConversationView[];
  readonly project: {
    readonly chronicle: {
      readonly currentCode: string;
      readonly historicalBackfill: string;
      readonly projection: string;
      readonly unavailableSources: readonly string[];
      readonly updatedAt: string;
    } | null;
    readonly name: string | null;
    readonly repositories: readonly {
      readonly connectionId: string;
      readonly displayName: string;
      readonly health: string;
      readonly provider: string;
      readonly repositoryUrl: string;
    }[];
    readonly synchronizedAt: string;
  };
  readonly providerProjection: {
    readonly conflictCount: number;
    readonly failedCount: number;
    readonly pendingCount: number;
    readonly state: "conflicted" | "current" | "failed" | "pending";
  };
  readonly review: {
    readonly inspections: Readonly<Record<string, ReviewRunInspection>>;
    readonly runs: Readonly<Record<string, ReviewRun>>;
    readonly series: {
      readonly currentReviewRunId: string | null;
      readonly currentReviewSubjectId: string;
      readonly reviewSeriesId: string;
      readonly revision: number;
      readonly updatedAt: string;
    };
    readonly subjects: Readonly<Record<string, ReviewSubject>>;
    readonly synchronizedAt: string;
  };
}

export interface ReviewMaterial {
  readonly change: SubjectChange;
  readonly material:
    | {
        readonly availability: "available";
        readonly body: string;
        readonly mediaType: string;
        readonly sha256: string;
        readonly sizeBytes: number;
        readonly source: string;
      }
    | {
        readonly availability: "unavailable";
        readonly reason: string;
      };
}

export interface FindingEvidence {
  readonly candidate: {
    readonly claim: string;
    readonly impact: string;
    readonly requestedEvidence: readonly string[];
    readonly severity: string;
    readonly uncertainty: readonly string[];
  };
  readonly finding: {
    readonly currentDisposition: {
      readonly disposition: string;
      readonly reason: string;
    };
    readonly evidenceGrade: string;
    readonly findingId: string;
    readonly revision: number;
    readonly severity: string;
  };
  readonly receipt: {
    readonly artifactReferences: readonly {
      readonly artifactId: string;
      readonly authorization: "authorized" | "denied";
      readonly availability:
        "available" | "expired" | "missing" | "quarantined";
      readonly contentReference: string;
      readonly mediaType: string;
      readonly reproducible: boolean;
      readonly retentionUntil: string;
      readonly sha256: string;
      readonly sizeBytes: number;
    }[];
    readonly knownUncertainty: readonly string[];
    readonly receiptDigest: string;
    readonly receiptId: string;
    readonly revision: number;
    readonly verificationAttempts: readonly {
      readonly attemptId: string;
      readonly disproofOutcome: string;
      readonly evidenceKind: string;
      readonly limitations: readonly string[];
      readonly proofOutcome: string;
      readonly reasoning: string;
    }[];
  };
}

interface DomainEnvelope<T> {
  readonly code?: string;
  readonly data?: T;
  readonly state: string;
  readonly value?: T;
}

function completedValue<T>(
  operationId: DiffclawOperationId,
  response: DomainEnvelope<T> | T,
): T {
  if (
    typeof response === "object" &&
    response !== null &&
    "state" in response
  ) {
    if (
      response.state !== "completed" &&
      response.state !== "cancelled" &&
      response.state !== "partial" &&
      response.state !== "resumed"
    ) {
      throw new Error(
        "code" in response && typeof response.code === "string"
          ? response.code
          : `Operation ${operationId} did not complete`,
      );
    }
    const value = response.data ?? response.value;
    if (value === undefined) {
      throw new TypeError(`Operation ${operationId} returned no value`);
    }
    return value;
  }
  return response;
}

function cookieValue(name: string): string | null {
  for (const candidate of document.cookie.split(";")) {
    const separator = candidate.indexOf("=");
    if (separator === -1) {
      continue;
    }
    if (candidate.slice(0, separator).trim() === name) {
      return candidate.slice(separator + 1).trim();
    }
  }
  return null;
}

function csrfToken(): string | null {
  return (
    sessionStorage.getItem("diffclaw.csrf-token") ??
    cookieValue("diffclaw_csrf")
  );
}

function browserOptions() {
  const csrf = csrfToken();
  return {
    baseUrl: window.location.origin,
    credentials: "same-origin" as const,
    ...(csrf === null ? {} : { csrfToken: csrf }),
  };
}

export async function beginBrowserLogin(
  organizationId: string,
): Promise<BrowserLoginChallenge> {
  const response = (await executeDiffclawOperation(
    browserOptions(),
    "beginHumanLogin",
    {
      body: {
        organizationId,
        redirectUri: `${window.location.origin}/v1/auth/github/callback`,
        surface: "browser",
      },
      context: { correlationId: crypto.randomUUID() },
    },
  )) as DomainEnvelope<BrowserLoginChallenge>;
  return completedValue("beginHumanLogin", response);
}

export async function inspectBrowserSession(): Promise<BrowserSession> {
  const response = (await executeDiffclawOperation(
    browserOptions(),
    "inspectSession",
    { context: { correlationId: crypto.randomUUID() } },
  )) as DomainEnvelope<BrowserSession>;
  return completedValue("inspectSession", response);
}

export function hasBrowserCsrfCookie(): boolean {
  return csrfToken() !== null;
}

export class CockpitApi {
  readonly #identity: CockpitIdentity;

  constructor(identity: CockpitIdentity) {
    this.#identity = identity;
  }

  #options() {
    const csrf = csrfToken();
    return {
      baseUrl: window.location.origin,
      credentials: "same-origin" as const,
      ...(this.#identity.authentication === "local-bootstrap"
        ? { localBootstrapActorId: this.#identity.actorId }
        : {}),
      ...(csrf === null ? {} : { csrfToken: csrf }),
    };
  }

  async #execute<T>(
    operationId: DiffclawOperationId,
    request: DiffclawOperationRequest = {},
  ): Promise<T> {
    const correlationId = crypto.randomUUID();
    const response = (await executeDiffclawOperation(
      this.#options(),
      operationId,
      {
        ...request,
        context: {
          actorId: this.#identity.actorId,
          correlationId,
          organizationId: this.#identity.organizationId,
          ...request.context,
          ...(request.context?.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: request.context.idempotencyKey }),
        },
      },
    )) as DomainEnvelope<T> | T;
    return completedValue(operationId, response);
  }

  command<T>(
    operationId: DiffclawOperationId,
    request: DiffclawOperationRequest,
  ): Promise<T> {
    return this.#execute(operationId, {
      ...request,
      context: {
        ...request.context,
        idempotencyKey: crypto.randomUUID(),
      },
    });
  }

  query<T>(
    operationId: DiffclawOperationId,
    request: DiffclawOperationRequest = {},
  ): Promise<T> {
    return this.#execute(operationId, request);
  }

  health(kind: "live" | "ready"): Promise<HealthStatus> {
    return this.#execute(kind === "live" ? "getLiveness" : "getReadiness");
  }

  session(): Promise<BrowserSession> {
    return this.#execute("inspectSession");
  }

  logout(): Promise<{ readonly sessionId: string }> {
    return this.#execute("logoutSession");
  }

  issueServiceCredential(
    body: ServiceCredentialRequest,
    idempotencyKey: string,
  ): Promise<ServiceCredentialGrant> {
    return this.#execute("issueServiceCredential", {
      body,
      context: { idempotencyKey },
    });
  }

  revokeServiceCredential(
    credentialId: string,
  ): Promise<{ readonly credentialId: string }> {
    return this.command("revokeServiceCredential", {
      path: { credentialId },
    });
  }

  readiness(): Promise<ChronicleReadiness> {
    return this.#execute("inspectChronicleReadiness", {
      path: { projectId: this.#identity.projectId },
    });
  }

  project(): Promise<{ readonly project: ProjectSummary }> {
    return this.#execute("inspectProject", {
      path: { projectId: this.#identity.projectId },
    });
  }

  projectPolicy(): Promise<{ readonly policy: ProjectPolicy }> {
    return this.query("inspectProjectPolicy", {
      path: { projectId: this.#identity.projectId },
    });
  }

  setDefaultReviewerProfile(
    policy: ProjectPolicy,
    reference: ReviewerProfileReference | null,
  ): Promise<{ readonly policy: ProjectPolicy }> {
    return this.command("changeProjectPolicy", {
      path: { projectId: this.#identity.projectId },
      body: {
        expectedRevision: policy.revision,
        policy: {
          defaultReviewerProfile: reference,
          allowedExternalIntegrations: policy.allowedExternalIntegrations,
          dataResidency: policy.dataResidency,
          maximumComputeUsd: policy.maximumComputeUsd,
          modelProviders: policy.modelProviders,
          reviewDepth: policy.reviewDepth,
          trustedRevision: policy.trustedRevision,
        },
      },
    });
  }

  backfill(): Promise<{ readonly backfill: ChronicleBackfill | null }> {
    return this.#execute("inspectChronicleBackfill", {
      path: { projectId: this.#identity.projectId },
    });
  }

  calibration(): Promise<ChronicleCalibrationInspection> {
    return this.#execute("inspectChronicleCalibration", {
      path: { projectId: this.#identity.projectId },
    });
  }

  learning(): Promise<ChronicleLearningInspection> {
    return this.#execute("inspectChronicleLearning", {
      path: { projectId: this.#identity.projectId },
    });
  }

  startLearningRun(body: {
    readonly budgetMicros: number;
    readonly mode: ChronicleLearningRun["mode"];
    readonly runId: string;
  }): Promise<{ readonly run: ChronicleLearningRun }> {
    return this.command("startChronicleLearningRun", {
      body,
      path: { projectId: this.#identity.projectId },
    });
  }

  cancelLearningRun(
    runId: string,
    expectedRevision: number,
  ): Promise<{ readonly run: ChronicleLearningRun }> {
    return this.command("cancelChronicleLearningRun", {
      body: { expectedRevision, reason: "operator-cancelled" },
      path: { projectId: this.#identity.projectId, runId },
    });
  }

  personalCoaching(): Promise<PersonalCoachingView> {
    return this.#execute("inspectPersonalCoaching", {
      path: { projectId: this.#identity.projectId },
    });
  }

  setPersonalCoachingConsent(body: {
    readonly enabled: boolean;
    readonly expectedRevision: number;
    readonly expiresAt: string;
    readonly reason: string;
  }): Promise<{ readonly consent: PersonalCoachingView["consent"] }> {
    return this.command("setPersonalCoachingConsent", {
      body,
      path: { projectId: this.#identity.projectId },
    });
  }

  correctPersonalCoachingMemory(
    memoryId: string,
    body: {
      readonly content: string;
      readonly expectedRevision: number;
      readonly reason: string;
    },
  ): Promise<{ readonly memory: PersonalCoachingMemory }> {
    return this.command("correctPersonalCoachingMemory", {
      body,
      path: { memoryId, projectId: this.#identity.projectId },
    });
  }

  deletePersonalCoachingMemory(
    memoryId: string,
    expectedRevision: number,
  ): Promise<unknown> {
    return this.command("deletePersonalCoachingMemory", {
      body: { expectedRevision, reason: "developer-deletion" },
      path: { memoryId, projectId: this.#identity.projectId },
    });
  }

  generatePersonalCoaching(
    objectivePolicyDigest: string,
  ): Promise<PersonalCoachingResponse> {
    return this.command("generatePersonalCoaching", {
      body: {
        objectivePolicyDigest,
        useId: `coaching-use:${crypto.randomUUID()}`,
      },
      path: { projectId: this.#identity.projectId },
    });
  }

  transitionLearningProposal(
    proposalId: string,
    body: {
      readonly evaluationPromotionId: string | null;
      readonly expectedRevision: number;
      readonly reason: string;
      readonly state: Exclude<LearningProposal["state"], "proposed">;
    },
  ): Promise<{ readonly proposal: LearningProposal }> {
    return this.command("transitionChronicleLearning", {
      body: {
        evidenceReference: body.evaluationPromotionId,
        expectedRevision: body.expectedRevision,
        reason: body.reason,
        state: body.state,
      },
      path: { projectId: this.#identity.projectId, proposalId },
    });
  }

  evaluationCorpora(): Promise<readonly EvaluationCorpus[]> {
    return this.query<{ readonly corpora: readonly EvaluationCorpus[] }>(
      "listEvaluationCorpora",
      { path: { projectId: this.#identity.projectId } },
    ).then((value) => value.corpora);
  }

  evaluationRuns(): Promise<readonly EvaluationRun[]> {
    return this.query<{ readonly runs: readonly EvaluationRun[] }>(
      "listEvaluationRuns",
      { path: { projectId: this.#identity.projectId } },
    ).then((value) => value.runs);
  }

  registerEvaluationCorpus(
    body: Readonly<Record<string, unknown>>,
  ): Promise<{ readonly corpus: EvaluationCorpus }> {
    return this.command("registerEvaluationCorpus", {
      body,
      path: { projectId: this.#identity.projectId },
    });
  }

  startEvaluationRun(
    body: Readonly<Record<string, unknown>>,
  ): Promise<{ readonly run: EvaluationRun }> {
    return this.command("startEvaluationRun", {
      body,
      path: { projectId: this.#identity.projectId },
    });
  }

  cancelEvaluationRun(
    runId: string,
    expectedRevision: number,
    reason: string,
  ): Promise<{ readonly run: EvaluationRun }> {
    return this.command("cancelEvaluationRun", {
      body: { expectedRevision, reason },
      path: { projectId: this.#identity.projectId, runId },
    });
  }

  resumeEvaluationRun(
    runId: string,
    expectedRevision: number,
    reason: string,
  ): Promise<{ readonly run: EvaluationRun }> {
    return this.command("resumeEvaluationRun", {
      body: { expectedRevision, reason },
      path: { projectId: this.#identity.projectId, runId },
    });
  }

  finalizeEvaluationRun(
    runId: string,
    expectedRevision: number,
  ): Promise<{ readonly run: EvaluationRun }> {
    return this.command("finalizeEvaluationRun", {
      body: { expectedRevision },
      path: { projectId: this.#identity.projectId, runId },
    });
  }

  decideEvaluationPromotion(
    body: Readonly<Record<string, unknown>>,
  ): Promise<{ readonly promotion: EvaluationPromotion }> {
    return this.command("decideEvaluationPromotion", {
      body,
      path: { projectId: this.#identity.projectId },
    });
  }

  advanceBackfill(
    backfillId: string,
    maximumRecords: number,
  ): Promise<ChronicleBackfillResult> {
    return this.command("advanceChronicleBackfill", {
      body: { maximumRecords },
      path: { backfillId, projectId: this.#identity.projectId },
    });
  }

  controlBackfill(
    backfillId: string,
    action: "cancel" | "pause" | "resume",
    expectedRevision: number,
    reason: string,
  ): Promise<ChronicleBackfillResult> {
    return this.command("controlChronicleBackfill", {
      body: { action, expectedRevision, reason },
      path: { backfillId, projectId: this.#identity.projectId },
    });
  }

  reviews(): Promise<{ readonly reviews: readonly CockpitReviewListItem[] }> {
    return this.#execute("listCockpitReviews", {
      path: { projectId: this.#identity.projectId },
    });
  }

  workspace(reviewSeriesId: string): Promise<CockpitReviewWorkspace> {
    return this.#execute("inspectCockpitReview", {
      path: { projectId: this.#identity.projectId, reviewSeriesId },
    });
  }

  material(reviewSubjectId: string, path: string): Promise<ReviewMaterial> {
    return this.#execute("inspectReviewSubjectMaterial", {
      path: { projectId: this.#identity.projectId, reviewSubjectId },
      query: { path },
    });
  }

  finding(findingId: string): Promise<FindingEvidence> {
    return this.#execute("inspectFinding", {
      path: { findingId, projectId: this.#identity.projectId },
    });
  }

  executeReviewRun(
    reviewRunId: string,
    expectedRevision: number,
  ): Promise<unknown> {
    return this.command("executeReviewRun", {
      body: { expectedRevision },
      path: { projectId: this.#identity.projectId, reviewRunId },
    });
  }

  startReviewRun(body: {
    readonly expectedSeriesRevision: number;
    readonly predecessorReviewRunId: string | null;
    readonly reviewRunId: string;
    readonly reviewSubjectId: string;
    readonly requestedReviewerProfile?: ReviewerProfileReference;
  }): Promise<unknown> {
    return this.command("startReviewRun", {
      body,
      path: { projectId: this.#identity.projectId },
    });
  }

  exportEvidenceArtifact(
    findingId: string,
    artifactId: string,
    receiptRevision: number,
  ): Promise<DiffclawArtifactExport> {
    return this.#execute("exportEvidenceArtifact", {
      path: {
        artifactId,
        findingId,
        projectId: this.#identity.projectId,
      },
      query: { receiptRevision },
    });
  }

  reviewAction(
    conversationId: string,
    body: Readonly<Record<string, unknown>>,
  ): Promise<{
    readonly action: {
      readonly resultReference: string | null;
      readonly outcomeCode: string;
      readonly state:
        "accepted" | "completed" | "denied" | "failed" | "partial";
    };
  }> {
    return this.command("performReviewConversationAction", {
      body,
      path: { conversationId, projectId: this.#identity.projectId },
    });
  }

  dispatchReviewProjection(
    conversationId: string,
    intentId: string,
  ): Promise<{
    readonly intent: {
      readonly state: string;
      readonly lastErrorCode: string | null;
    };
  }> {
    return this.command("dispatchReviewConversationProjection", {
      body: {},
      path: { conversationId, intentId, projectId: this.#identity.projectId },
    });
  }

  savePrivateNote(
    conversationId: string,
    noteId: string,
    body: Readonly<Record<string, unknown>>,
  ): Promise<unknown> {
    return this.command("recordPrivateReviewNote", {
      body,
      path: { conversationId, noteId, projectId: this.#identity.projectId },
    });
  }
}

const IDENTITY_STORAGE_KEY = "diffclaw.cockpit-identity";
const RECOVERY_SCOPE_STORAGE_KEY = "diffclaw.cockpit-recovery-scope";

export function loadIdentity(): CockpitIdentity | null {
  const value = localStorage.getItem(IDENTITY_STORAGE_KEY);
  if (value === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(value) as Partial<CockpitIdentity>;
    return typeof parsed.actorId === "string" &&
      (parsed.authentication === "local-bootstrap" ||
        parsed.authentication === "session") &&
      typeof parsed.organizationId === "string" &&
      typeof parsed.projectId === "string"
      ? {
          actorId: parsed.actorId,
          authentication: parsed.authentication,
          organizationId: parsed.organizationId,
          projectId: parsed.projectId,
        }
      : null;
  } catch {
    return null;
  }
}

export function loadRecoveryScope(): CockpitScope | null {
  const value = localStorage.getItem(RECOVERY_SCOPE_STORAGE_KEY);
  if (value === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(value) as Partial<CockpitScope>;
    return typeof parsed.organizationId === "string" &&
      typeof parsed.projectId === "string"
      ? {
          organizationId: parsed.organizationId,
          projectId: parsed.projectId,
        }
      : null;
  } catch {
    return null;
  }
}

export function expireSavedIdentity(identity: CockpitIdentity): CockpitScope {
  const scope = {
    organizationId: identity.organizationId,
    projectId: identity.projectId,
  };
  localStorage.setItem(RECOVERY_SCOPE_STORAGE_KEY, JSON.stringify(scope));
  localStorage.removeItem(IDENTITY_STORAGE_KEY);
  return scope;
}

export function clearSavedIdentity(): void {
  localStorage.removeItem(IDENTITY_STORAGE_KEY);
  localStorage.removeItem(RECOVERY_SCOPE_STORAGE_KEY);
}

export function saveIdentity(identity: CockpitIdentity): void {
  localStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(identity));
  localStorage.removeItem(RECOVERY_SCOPE_STORAGE_KEY);
}
